import { ROBOT_LEVELS, type RobotLevel } from '@chessmate/protocol';
import { Button, Card, Input } from '@chessmate/ui';
// apps/app/src/screens/OnlineLobby.tsx
import { useEffect, useRef, useState } from 'react';
import { RobotPicker } from '../components/RobotPicker.js';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { useT } from '../i18n/useT.js';
import { type GameSummary, api } from '../services/api.js';
import { subscribeMatchmaking } from '../services/sse.js';
import type { OnlineOpenInput } from '../state/online-store.js';
import { useProfileStore } from '../state/profile-store.js';
import { useProgressStore } from '../state/progress-store.js';

/** A Card on the glass panel, with room to breathe. */
const PANEL_CLASS_CARD = `${PANEL_CLASS} flex flex-col gap-3 p-5 sm:p-6`;

export interface OnlineLobbyProps {
  readonly onPlay: (input: OnlineOpenInput) => void;
  readonly onExit: () => void;
}

export function OnlineLobby({ onPlay, onExit }: OnlineLobbyProps) {
  const { t } = useT('common');
  const { t: tNav } = useT('navigation');
  const profile = useProfileStore((s) => s.profile);
  const setProfile = useProfileStore((s) => s.setProfile);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [rating, setRating] = useState<{ rating: number; deviation: number } | null>(null);
  const [games, setGames] = useState<GameSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'casual' | 'rated'>('casual');
  const [variant, setVariant] = useState<'chess' | 'backgammon'>('chess');
  const [level, setLevel] = useState<RobotLevel>(ROBOT_LEVELS[0] as RobotLevel);
  const [friend, setFriend] = useState<{ gameId: string } | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [queued, setQueued] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);
  const queueUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => () => queueUnsubRef.current?.(), []);

  const joinQueue = async () => {
    if (!profile) return;
    setQueueBusy(true);
    setError(null);
    try {
      const res = await api.joinMatchmaking(profile.token, {
        elo: Math.round(rating?.rating ?? 1500),
        variant,
      });
      if (res.gameId) {
        onPlay({ kind: 'resume', token: profile.token, gameId: res.gameId });
        return;
      }
      queueUnsubRef.current = subscribeMatchmaking(profile.token, {
        onEvent: (event) => {
          if (event.type === 'match:paired') {
            queueUnsubRef.current?.();
            queueUnsubRef.current = null;
            onPlay({ kind: 'resume', token: profile.token, gameId: event.gameId });
          }
        },
      });
      setQueued(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join the queue.');
    } finally {
      setQueueBusy(false);
    }
  };

  const cancelQueue = async () => {
    queueUnsubRef.current?.();
    queueUnsubRef.current = null;
    setQueued(false);
    if (!profile) return;
    api.leaveMatchmaking(profile.token).catch(() => {});
  };

  useEffect(() => {
    if (!profile) return;
    api
      .fetchProfile(profile.token)
      .then((p) => setRating(p.rating))
      .catch(() => {});
    api
      .listGames(profile.token)
      .then(setGames)
      .catch(() => {});
    api
      .fetchLessonProgress(profile.token)
      .then((best) => useProgressStore.getState().mergeServerBest(best))
      .catch(() => {});
  }, [profile]);

  const createProfile = async () => {
    if (name.trim().length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const minted = await api.createProfile(name.trim());
      setProfile({ playerId: minted.playerId, name: minted.name, token: minted.token });
      setRating({ rating: 1500, deviation: 350 });
      setGames([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a profile.');
    } finally {
      setCreating(false);
    }
  };

  const createFriendGame = async () => {
    if (!profile) return;
    setCreating(true);
    setError(null);
    try {
      const game = await api.createGame(profile.token, { opponent: 'human', mode, variant });
      setFriend({ gameId: game.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a game.');
    } finally {
      setCreating(false);
    }
  };

  if (!profile) {
    return (
      <ScreenLayout center width="wide" data-testid="online-lobby" data-appdom-scope="lobby">
        <div className={`${PANEL_CLASS} grid w-full max-w-5xl overflow-hidden lg:grid-cols-2`}>
          <img src="/art/spot-friends.webp" alt={t('art-alt-friends')} className="h-40 w-full object-cover sm:h-52 lg:h-full lg:min-h-[380px]" />
          <div className="flex flex-col justify-center gap-5 p-6 sm:p-10 lg:p-12">
            <h1 className="font-display text-h1 font-bold text-fg lg:text-5xl">{tNav('play-online')}</h1>
            <h2 className="font-display text-h3 font-bold text-fg">{t('who-are-you')}</h2>
          <div className="flex flex-col gap-3">
            <Input
              data-testid="profile-name"
              aria-label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="h-14 text-xl font-semibold"
            />
            <Button
              data-testid="profile-create"
              data-appdom-id="profile-create"
              data-appdom-type="Button"
              loading={creating}
              onClick={() => void createProfile()}
              disabled={name.trim().length === 0}
            >
              {creating ? t('creating') : t('start-playing')}
            </Button>
          </div>
          </div>
        </div>
        {error && (
          <p className={`${PANEL_CLASS} px-4 py-2 font-bold text-red-700 dark:text-red-300`} data-testid="lobby-error">
            {error}
          </p>
        )}
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout width="wide" data-testid="online-lobby" data-appdom-scope="lobby">
      <PageHeader
        title={t('online')}
        action={
          <Button
            variant="secondary"
            data-testid="lobby-exit"
            data-appdom-id="lobby-exit"
            data-appdom-type="Button"
            onClick={onExit}
          >
            {t('exit')}
          </Button>
        }
      />
      <div className="grid items-start gap-6 lg:grid-cols-2 lg:gap-8">
      <div className="flex flex-col gap-6">
      <Card title={profile.name} className={PANEL_CLASS_CARD}>
        <p className="font-display text-hero font-bold text-accent" data-testid="rating-value">
          {Math.round(rating?.rating ?? 1500)}
        </p>
        <p className="text-sm font-semibold text-fg-muted" data-testid="rating-deviation">
          {rating ? `${t('deviation')} ${Math.round(rating.deviation)}` : t('new-player')}
        </p>
      </Card>
      <div className="flex gap-2">
        {(['casual', 'rated'] as const).map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`mode-${m}`}
            data-appdom-id={`mode-${m}`}
            data-appdom-type="Button"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={`flex-1 min-h-[52px] rounded-xl border px-4 py-2 font-display text-lg font-semibold lg:text-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              mode === m
                ? 'border-accent bg-accent text-accent-fg shadow-sm'
                : 'border-border bg-surface text-fg-muted hover:text-fg'
            }`}
          >
            {m === 'casual' ? t('casual') : t('rated')}
          </button>
        ))}
      </div>
      {/* Game choice for friend games — online backgammon landed 2026-10-04
          (casual human-vs-human, docs/proposals/backgammon-online.md). */}
      <div className="flex gap-2">
        {(['chess', 'backgammon'] as const).map((v) => (
          <button
            key={v}
            type="button"
            data-testid={`variant-${v}`}
            data-appdom-id={`variant-${v}`}
            data-appdom-type="Button"
            aria-pressed={variant === v}
            onClick={() => setVariant(v)}
            className={`flex-1 min-h-[52px] rounded-xl border px-4 py-2 font-display text-lg font-semibold lg:text-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              variant === v
                ? 'border-accent bg-accent text-accent-fg shadow-sm'
                : 'border-border bg-surface text-fg-muted hover:text-fg'
            }`}
          >
            {v === 'chess' ? 'Chess' : 'Backgammon'}
          </button>
        ))}
      </div>

      </div>
      <div className="flex flex-col gap-6">
      <Card title={t('play-vs-the-robot')} className={PANEL_CLASS_CARD}>
        <RobotPicker
          selected={level}
          onSelect={setLevel}
          onStart={() =>
            onPlay({
              kind: 'create',
              token: profile.token,
              input: { opponent: 'ai', mode, elo: level.elo },
            })
          }
        />
      </Card>

      <Card title={t('play-vs-a-friend')} className={`${PANEL_CLASS_CARD} glass glass-light lift`}>
        {error && (
          <p className="font-bold text-berry" data-testid="lobby-error">
            {error}
          </p>
        )}
        {friend ? (
          <div className="flex flex-col gap-3 rounded-2xl bg-sun/20 p-3">
            <p className="text-sm font-bold text-ink/60">
              {t('share-this-code')}
            </p>
            <p className="text-3xl font-black tracking-widest text-berry" data-testid="room-code">
              {friend.gameId}
            </p>
            <Button
              data-testid="start-friend-game"
              onClick={() =>
                onPlay({ kind: 'resume', token: profile.token, gameId: friend.gameId })
              }
            >
              {t('start-game')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              data-testid="new-friend-game"
              onClick={() => void createFriendGame()}
              disabled={creating}
              className="w-full"
            >
              {t('create-friend-game')}
            </Button>
            <div className="flex gap-2">
              <input
                data-testid="join-code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder={t('enter-a-code')}
                className="min-h-[48px] min-w-0 flex-1 rounded-2xl border-2 border-sun/40 bg-white px-3 py-2 text-base font-semibold text-ink dark:text-cream"
              />
              <Button
                data-testid="join-submit"
                disabled={joinCode.trim().length === 0}
                onClick={() =>
                  onPlay({ kind: 'join', token: profile.token, gameId: joinCode.trim() })
                }
              >
                {t('join')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card title={t('quick-match')} className={`${PANEL_CLASS_CARD} glass glass-light`}>
        <p className="text-base font-semibold text-fg-muted">
          Play a live game against a player near your rating.
        </p>
        {queued ? (
          <div
            className="mt-3 flex flex-col gap-3 rounded-2xl bg-sun/20 p-3"
            data-testid="quick-match-queued"
          >
            <p className="font-bold text-ink" data-testid="quick-match-status">
              {t('looking-for-game')}
            </p>
            <Button
              variant="secondary"
              data-testid="quick-match-cancel"
              onClick={() => void cancelQueue()}
              disabled={queueBusy}
            >
              {t('cancel')}
            </Button>
          </div>
        ) : (
          <Button
            className="mt-3 w-full"
            data-testid="quick-match"
            onClick={() => void joinQueue()}
            disabled={queueBusy || !profile}
          >
            {queueBusy ? t('joining') : t('play-quick-match')}
          </Button>
        )}
      </Card>

      <Card title={t('my-games')} className={PANEL_CLASS_CARD}>
        {games.length === 0 ? (
          <p className="text-base font-semibold text-fg-muted">
            {t('no-games-yet')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {games.map((g) => (
              <li
                key={g.id}
                className="flex items-center justify-between rounded-2xl bg-white/70 p-2"
                data-testid={`game-row-${g.id}`}
              >
                <span className="font-bold text-ink">
                  {g.opponent === 'ai' ? t('vs-robot') : t('vs-friend')} ·{' '}
                  {g.rated ? t('rated') : t('casual')} · {g.status}
                </span>
                <Button
                  variant="ghost"
                  data-testid={`game-resume-${g.id}`}
                  onClick={() => onPlay({ kind: 'resume', token: profile.token, gameId: g.id })}
                  size="sm"
                >
                  {t('resume')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      </div>
      </div>
    </ScreenLayout>
  );
}
